/*
 *
 *  * Copyright (c) [hoarfroster-frontend] 2026. All Rights Reserved.
 *  *
 *  * Last Modified on Jun 21, 2026 by hoarfroster
 *
 */

import React, { JSX, useEffect, useState } from 'react';
import I18N, { I18NLocaleNames, I18NLocales, Locale } from '@/utils/i18n.base';
import { getStorageItem } from '@utils/storage.ts';

/**
 * Renders a language switcher UI for selecting the application locale.
 *
 * @returns {JSX.Element} The language switcher component.
 */
export default function LanguageSwitcher(): JSX.Element {
    const [currentLocale, setCurrentLocaleState] = useState<Locale>(() => {
        const stored = getStorageItem('locale');
        if (stored && I18NLocales.includes(stored)) return stored as Locale;
        return 'zh-CN';
    });

    useEffect(() => {
        I18N.setLocale(currentLocale);
    }, [currentLocale]);

    const handleLanguageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value as Locale;
        setCurrentLocaleState(value);
        I18N.setLocale(value);
        window.location.reload();
    };

    return (
        <div>
            <div className="space-y-2.5">
                {I18NLocales.map((locale, index) => {
                    const isActive = currentLocale === locale;
                    return (
                        <label
                            key={locale}
                            className={`flex cursor-pointer items-center gap-3 rounded-md border px-3.5 py-3 transition ${
                                isActive
                                    ? 'border-theme-active bg-theme-active/10'
                                    : 'border-theme-border bg-theme-card-bg hover:border-theme-active/60 hover:bg-theme-hover'
                            } `}
                        >
                            <input
                                type="radio"
                                name="language"
                                value={locale}
                                checked={isActive}
                                onChange={handleLanguageChange}
                                className="sr-only"
                            />
                            <div
                                className={`flex h-4 w-4 items-center justify-center rounded-full border-2 transition ${
                                    isActive
                                        ? 'border-theme-active bg-theme-active'
                                        : 'border-theme-border bg-theme-card-bg'
                                } `}
                            >
                                {isActive && <div className="bg-theme-card-bg h-2 w-2 rounded-full"></div>}
                            </div>
                            <span
                                className={`text-sm font-semibold transition-colors ${
                                    isActive ? 'text-theme-active' : 'text-theme-text'
                                } `}
                            >
                                {I18NLocaleNames[index]}
                            </span>
                        </label>
                    );
                })}
            </div>
        </div>
    );
}
